/**
 * Planning Workspace Defaults Store
 *
 * Reads from and writes to public.planning_workspace_defaults (migration 546+).
 * One row per workspace; pre-fills the "Create Scenario" form and Item Effort bands.
 *
 * §5 build contract: cross-user/cross-session state is DB-backed.
 */

import { create } from 'zustand';
import { getSupabaseClient } from '../../lib/supabaseClient';
import {
  WORK_SIZE_HOURS,
  parseItemEffortBandHours,
  type WorkSizeHoursOverrides,
} from '../../lib/itemToPlanningAdapter';

export type PlanningModeDefault = 'priority_estimate' | 'work_units' | 'hybrid_remaining';

export interface PlanningDefaults {
  planningWeeks: number;
  bufferPct: number;
  weeklyCapacityHours: number;
  planningMode: PlanningModeDefault;
  defaultResourceType: string;
  defaultWuHours: number;
  /** Hours midpoints for Work Size / altitude 1–5 → Item Effort CU. */
  itemEffortBandHours: WorkSizeHoursOverrides;
}

const SYSTEM_DEFAULTS: PlanningDefaults = {
  planningWeeks: 12,
  bufferPct: 20,
  weeklyCapacityHours: 40,
  planningMode: 'priority_estimate',
  defaultResourceType: 'engineering',
  defaultWuHours: 8,
  itemEffortBandHours: { ...WORK_SIZE_HOURS },
};

interface PlanningDefaultsState {
  defaults: PlanningDefaults;
  loading: boolean;
  error: string | null;

  load: (workspaceId: string) => Promise<void>;
  setDefaults: (workspaceId: string, patch: Partial<PlanningDefaults>) => Promise<void>;
  resetDefaults: (workspaceId: string) => Promise<void>;
}

function rowToDefaults(row: Record<string, unknown>): PlanningDefaults {
  const parsedBands = parseItemEffortBandHours(row.item_effort_band_hours);
  return {
    planningWeeks: Number(row.planning_weeks ?? SYSTEM_DEFAULTS.planningWeeks),
    bufferPct: Number(row.buffer_pct ?? SYSTEM_DEFAULTS.bufferPct),
    weeklyCapacityHours: Number(row.weekly_capacity_hours ?? SYSTEM_DEFAULTS.weeklyCapacityHours),
    planningMode: (row.planning_mode as PlanningModeDefault) ?? SYSTEM_DEFAULTS.planningMode,
    defaultResourceType: (row.default_resource_type as string) ?? SYSTEM_DEFAULTS.defaultResourceType,
    defaultWuHours: Number(row.default_wu_hours ?? SYSTEM_DEFAULTS.defaultWuHours),
    itemEffortBandHours: { ...WORK_SIZE_HOURS, ...parsedBands },
  };
}

function defaultsToRow(d: Partial<PlanningDefaults>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (d.planningWeeks !== undefined) row.planning_weeks = d.planningWeeks;
  if (d.bufferPct !== undefined) row.buffer_pct = d.bufferPct;
  if (d.weeklyCapacityHours !== undefined) row.weekly_capacity_hours = d.weeklyCapacityHours;
  if (d.planningMode !== undefined) row.planning_mode = d.planningMode;
  if (d.defaultResourceType !== undefined) row.default_resource_type = d.defaultResourceType;
  if (d.defaultWuHours !== undefined) row.default_wu_hours = d.defaultWuHours;
  if (d.itemEffortBandHours !== undefined) {
    row.item_effort_band_hours = { ...WORK_SIZE_HOURS, ...d.itemEffortBandHours };
  }
  return row;
}

/** Current workspace Item Effort band hours (system defaults until load completes). */
export function getItemEffortBandHours(): WorkSizeHoursOverrides {
  return usePlanningDefaultsStore.getState().defaults.itemEffortBandHours;
}

export const usePlanningDefaultsStore = create<PlanningDefaultsState>()((set) => ({
  defaults: SYSTEM_DEFAULTS,
  loading: false,
  error: null,

  load: async (workspaceId) => {
    if (!workspaceId) return;
    set({ loading: true, error: null });
    try {
      const { data, error } = await getSupabaseClient()
        .from('planning_workspace_defaults')
        .select('*')
        .eq('workspace_id', workspaceId)
        .maybeSingle();
      if (error) throw error;
      set({ defaults: data ? rowToDefaults(data as Record<string, unknown>) : SYSTEM_DEFAULTS });
    } catch (err) {
      const msg = err instanceof Error ? err.message : (err as { message?: string })?.message ?? String(err);
      set({ error: msg });
    } finally {
      set({ loading: false });
    }
  },

  setDefaults: async (workspaceId, patch) => {
    if (!workspaceId) return;
    // Optimistic update
    set((s) => ({ defaults: { ...s.defaults, ...patch } }));
    const row = { workspace_id: workspaceId, ...defaultsToRow(patch), updated_at: new Date().toISOString() };
    const { error } = await getSupabaseClient()
      .from('planning_workspace_defaults')
      .upsert(row, { onConflict: 'workspace_id' });
    if (error) {
      // Roll back on failure
      set({ error: error.message });
    }
  },

  resetDefaults: async (workspaceId) => {
    if (!workspaceId) return;
    set({ defaults: SYSTEM_DEFAULTS });
    const row = { workspace_id: workspaceId, ...defaultsToRow(SYSTEM_DEFAULTS), updated_at: new Date().toISOString() };
    const { error } = await getSupabaseClient()
      .from('planning_workspace_defaults')
      .upsert(row, { onConflict: 'workspace_id' });
    if (error) set({ error: error.message });
  },
}));
